import React from 'react';
import { MediaFile } from '../media/types';
import AuthenticatedImage from '../media/AuthenticatedImage';
import { Heart, MessageCircle, Send, Bookmark, MoreHorizontal, Verified } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

interface MobilePreviewProps {
  platform: 'instagram' | 'facebook' | 'youtube' | 'twitter';
  contentType?: string;
  title?: string;
  content?: string;
  mediaFiles: MediaFile[];
  clientId: string;
  brandName: string;
}

const MobilePreview: React.FC<MobilePreviewProps> = ({
  platform,
  contentType = 'feed',
  title,
  content,
  mediaFiles,
  clientId,
  brandName
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Theme-aware colors for each platform
  const getThemeColors = (platform: string) => {
    if (isDark) {
      return {
        instagram: {
          bg: 'bg-black',
          cardBg: 'bg-gray-900',
          text: 'text-white',
          textSecondary: 'text-gray-400',
          border: 'border-gray-800',
          iconColor: 'text-white'
        },
        facebook: {
          bg: 'bg-gray-900',
          cardBg: 'bg-gray-800',
          text: 'text-white',
          textSecondary: 'text-gray-400',
          border: 'border-gray-700',
          iconColor: 'text-white'
        },
        youtube: {
          bg: 'bg-gray-900',
          cardBg: 'bg-black',
          text: 'text-white',
          textSecondary: 'text-gray-400',
          border: 'border-gray-800',
          iconColor: 'text-white'
        },
        twitter: {
          bg: 'bg-black',
          cardBg: 'bg-gray-900',
          text: 'text-white',
          textSecondary: 'text-gray-400',
          border: 'border-gray-800',
          iconColor: 'text-white'
        }
      };
    } else {
      return {
        instagram: {
          bg: 'bg-white',
          cardBg: 'bg-white',
          text: 'text-black',
          textSecondary: 'text-gray-600',
          border: 'border-gray-200',
          iconColor: 'text-black'
        },
        facebook: {
          bg: 'bg-gray-50',
          cardBg: 'bg-white',
          text: 'text-black',
          textSecondary: 'text-gray-600',
          border: 'border-gray-200',
          iconColor: 'text-gray-700'
        },
        youtube: {
          bg: 'bg-gray-100',
          cardBg: 'bg-white',
          text: 'text-black',
          textSecondary: 'text-gray-700',
          border: 'border-gray-200',
          iconColor: 'text-red-600'
        },
        twitter: {
          bg: 'bg-white',
          cardBg: 'bg-white',
          text: 'text-black',
          textSecondary: 'text-gray-600',
          border: 'border-gray-200',
          iconColor: 'text-gray-700'
        }
      };
    }
  };

  // Ensure we have a valid platform and fallback to instagram if not
  const validPlatform = ['instagram', 'facebook', 'youtube', 'twitter'].includes(platform) ? platform : 'instagram';
  const colors = getThemeColors(validPlatform)[validPlatform];
  const renderInstagramPost = () => {
    if (contentType === 'story') {
      return (
        <div className="bg-black h-full relative">
          {/* Story Image */}
          {mediaFiles.length > 0 && (
            <div className="h-full w-full relative">
              <AuthenticatedImage
                src={mediaFiles[0].thumbnail_url || mediaFiles[0].file_url}
                alt={mediaFiles[0].original_filename}
                className="w-full h-full object-cover"
                clientId={clientId}
              />
              {/* Story overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/30"></div>
            </div>
          )}
          
          {/* Story Header */}
          <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center border-2 border-white">
                <span className="text-white font-bold text-xs">{brandName.charAt(0)}</span>
              </div>
              <span className="text-white font-semibold text-sm">{brandName.toLowerCase().replace(/\s+/g, '')}</span>
              <span className="text-white/70 text-xs">now</span>
            </div>
            <MoreHorizontal className="w-5 h-5 text-white" />
          </div>

          {/* Story Content */}
          {content && (
            <div className="absolute bottom-20 left-4 right-4">
              <p className="text-white text-sm font-medium line-clamp-2">
                {content.length > 80 ? `${content.substring(0, 80)}...` : content}
              </p>
            </div>
          )}

          {/* Story Actions */}
          <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Heart className="w-6 h-6 text-white" />
              <MessageCircle className="w-6 h-6 text-white" />
              <Send className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      );
    }

    if (contentType === 'reel') {
      return (
        <div className="bg-black h-full relative">
          {/* Reel Video/Image */}
          {mediaFiles.length > 0 && (
            <div className="h-full w-full relative">
              <AuthenticatedImage
                src={mediaFiles[0].thumbnail_url || mediaFiles[0].file_url}
                alt={mediaFiles[0].original_filename}
                className="w-full h-full object-cover"
                clientId={clientId}
              />
              {/* Play button overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <div className="w-0 h-0 border-l-[12px] border-l-white border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent ml-1"></div>
                </div>
              </div>
            </div>
          )}

          {/* Reel Actions (Right side) */}
          <div className="absolute right-3 bottom-20 flex flex-col items-center space-y-6">
            <div className="flex flex-col items-center">
              <Heart className="w-7 h-7 text-white" />
              <span className="text-white text-xs mt-1">1.2K</span>
            </div>
            <div className="flex flex-col items-center">
              <MessageCircle className="w-7 h-7 text-white" />
              <span className="text-white text-xs mt-1">89</span>
            </div>
            <div className="flex flex-col items-center">
              <Send className="w-7 h-7 text-white" />
            </div>
            <div className="flex flex-col items-center">
              <Bookmark className="w-7 h-7 text-white" />
            </div>
          </div>

          {/* Reel Info (Bottom) */}
          <div className="absolute bottom-4 left-3 right-16">
            <div className="flex items-center space-x-2 mb-2">
              <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xs">{brandName.charAt(0)}</span>
              </div>
              <span className="text-white font-semibold text-sm">{brandName.toLowerCase().replace(/\s+/g, '')}</span>
            </div>
            {content && (
              <p className="text-white text-sm line-clamp-2">
                {content.length > 60 ? `${content.substring(0, 60)}...` : content}
              </p>
            )}
          </div>
        </div>
      );
    }

    // Default Feed Post
    return (
      <div className={colors.cardBg}>
        {/* Instagram Header */}
        <div className={`flex items-center justify-between p-3 border-b ${colors.border}`}>
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-xs">{brandName.charAt(0)}</span>
            </div>
            <div className="flex items-center space-x-1">
              <span className={`font-semibold text-sm ${colors.text}`}>{brandName.toLowerCase().replace(/\s+/g, '')}</span>
              <Verified className="w-3 h-3 text-blue-500" />
            </div>
          </div>
          <MoreHorizontal className={`w-5 h-5 ${colors.iconColor}`} />
        </div>

        {/* Instagram Image */}
        {mediaFiles.length > 0 && (
          <div className={`aspect-square ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
            <AuthenticatedImage
              src={mediaFiles[0].thumbnail_url || mediaFiles[0].file_url}
              alt={mediaFiles[0].original_filename}
              className="w-full h-full object-cover"
              clientId={clientId}
            />
          </div>
        )}

        {/* Instagram Actions */}
        <div className="p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-4">
              <Heart className={`w-6 h-6 ${colors.iconColor}`} />
              <MessageCircle className={`w-6 h-6 ${colors.iconColor}`} />
              <Send className={`w-6 h-6 ${colors.iconColor}`} />
            </div>
            <Bookmark className={`w-6 h-6 ${colors.iconColor}`} />
          </div>

          <div className={`text-sm ${colors.text} line-clamp-3`}>
            <span className="font-semibold">{brandName.toLowerCase().replace(/\s+/g, '')}</span>
            {content && (
              <span className="ml-1">
                {content.length > 120 ? `${content.substring(0, 120)}...` : content}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderFacebookPost = () => {
    if (contentType === 'story') {
      return (
        <div className="bg-black h-full relative">
          {/* Facebook Story Image */}
          {mediaFiles.length > 0 && (
            <div className="h-full w-full relative">
              <AuthenticatedImage
                src={mediaFiles[0].thumbnail_url || mediaFiles[0].file_url}
                alt={mediaFiles[0].original_filename}
                className="w-full h-full object-cover"
                clientId={clientId}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/30"></div>
            </div>
          )}
          
          {/* Facebook Story Header */}
          <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center border-2 border-white">
                <span className="text-white font-bold text-xs">{brandName.charAt(0)}</span>
              </div>
              <span className="text-white font-semibold text-sm">{brandName}</span>
              <span className="text-white/70 text-xs">now</span>
            </div>
            <MoreHorizontal className="w-5 h-5 text-white" />
          </div>

          {/* Facebook Story Content */}
          {content && (
            <div className="absolute bottom-20 left-4 right-4">
              <p className="text-white text-sm font-medium line-clamp-2">
                {content.length > 80 ? `${content.substring(0, 80)}...` : content}
              </p>
            </div>
          )}
        </div>
      );
    }

    // Default Facebook Feed Post
    return (
      <div className={colors.cardBg}>
        {/* Facebook Header */}
        <div className="flex items-center justify-between p-3">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">{brandName.charAt(0)}</span>
            </div>
            <div>
              <div className="flex items-center space-x-1">
                <span className={`font-semibold text-sm ${colors.text}`}>{brandName}</span>
                <Verified className="w-3 h-3 text-blue-500" />
              </div>
              <span className={`text-xs ${colors.textSecondary}`}>Just now · 🌍</span>
            </div>
          </div>
          <MoreHorizontal className={`w-5 h-5 ${colors.iconColor}`} />
        </div>

        {/* Facebook Content */}
        {content && (
          <div className="px-3 pb-3">
            <p className={`text-sm ${colors.text} line-clamp-4`}>
              {content.length > 150 ? `${content.substring(0, 150)}...` : content}
            </p>
          </div>
        )}

        {/* Facebook Image */}
        {mediaFiles.length > 0 && (
          <div className={isDark ? 'bg-gray-800' : 'bg-gray-100'}>
            <AuthenticatedImage
              src={mediaFiles[0].thumbnail_url || mediaFiles[0].file_url}
              alt={mediaFiles[0].original_filename}
              className="w-full max-h-80 object-contain"
              clientId={clientId}
            />
          </div>
        )}

        {/* Facebook Actions */}
        <div className={`border-t ${colors.border} p-3`}>
          <div className={`flex items-center justify-between ${colors.iconColor}`}>
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-1">
                <Heart className="w-5 h-5" />
                <span className="text-sm">Like</span>
              </div>
              <div className="flex items-center space-x-1">
                <MessageCircle className="w-5 h-5" />
                <span className="text-sm">Comment</span>
              </div>
              <div className="flex items-center space-x-1">
                <Send className="w-5 h-5" />
                <span className="text-sm">Share</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTwitterPost = () => (
    <div className={colors.cardBg}>
      {/* Twitter Header */}
      <div className="flex items-start space-x-3 p-3">
        <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
          <span className="text-white font-bold text-sm">{brandName.charAt(0)}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-1">
            <span className={`font-bold text-sm ${colors.text}`}>{brandName}</span>
            <Verified className="w-4 h-4 text-blue-500" />
            <span className={`text-sm ${colors.textSecondary}`}>@{brandName.toLowerCase().replace(/\s+/g, '')}</span>
            <span className={`text-sm ${colors.textSecondary}`}>·</span>
            <span className={`text-sm ${colors.textSecondary}`}>now</span>
          </div>
          
          {/* Twitter Content */}
          {content && (
            <div className="mt-1">
              <p className={`text-sm ${colors.text} line-clamp-3`}>
                {content.length > 280 ? `${content.substring(0, 280)}...` : content}
              </p>
            </div>
          )}

          {/* Twitter Image */}
          {mediaFiles.length > 0 && (
            <div className={`mt-3 rounded-2xl overflow-hidden border ${colors.border}`}>
              <AuthenticatedImage
                src={mediaFiles[0].thumbnail_url || mediaFiles[0].file_url}
                alt={mediaFiles[0].original_filename}
                className="w-full max-h-64 object-cover"
                clientId={clientId}
              />
            </div>
          )}

          {/* Twitter Actions */}
          <div className="flex items-center justify-between mt-3 max-w-md">
            <div className={`flex items-center space-x-1 ${colors.textSecondary}`}>
              <MessageCircle className="w-4 h-4" />
              <span className="text-sm">24</span>
            </div>
            <div className={`flex items-center space-x-1 ${colors.textSecondary}`}>
              <div className="w-4 h-4 flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                  <path d="M4.5 3.88l4.432 4.14-1.364 1.46L5.5 7.55V16c0 1.1.896 2 2 2H13v2H7.5c-2.209 0-4-1.791-4-4V7.55L1.432 9.48.068 8.02 4.5 3.88zM16.5 20.12L12.068 16l1.364-1.46L16.5 16.45V8c0-1.1-.896-2-2-2H11V4h3.5c2.209 0 4 1.791 4 4v8.45l2.068-1.93 1.364 1.46-4.432 4.12z"/>
                </svg>
              </div>
              <span className="text-sm">12</span>
            </div>
            <div className={`flex items-center space-x-1 ${colors.textSecondary}`}>
              <Heart className="w-4 h-4" />
              <span className="text-sm">89</span>
            </div>
            <div className={`flex items-center space-x-1 ${colors.textSecondary}`}>
              <Send className="w-4 h-4" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderYouTubePost = () => (
    <div className={colors.cardBg}>
      {/* YouTube Video Player */}
      <div className="relative bg-black aspect-video">
        {mediaFiles && mediaFiles.length > 0 ? (
          <div className="relative w-full h-full">
            <AuthenticatedImage
              src={mediaFiles[0].thumbnail_url || mediaFiles[0].file_url}
              alt="Video thumbnail"
              className="w-full h-full object-cover"
              clientId={clientId}
            />
            {/* Play Button Overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center shadow-lg">
                <div className="w-0 h-0 border-l-[20px] border-l-white border-t-[12px] border-t-transparent border-b-[12px] border-b-transparent ml-1"></div>
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center">
              <div className="w-0 h-0 border-l-[20px] border-l-white border-t-[12px] border-t-transparent border-b-[12px] border-b-transparent ml-1"></div>
            </div>
          </div>
        )}
      </div>

      {/* YouTube Video Info */}
      <div className="p-3">
        <div className="flex items-start space-x-3">
          <div className="w-9 h-9 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-xs">{brandName.charAt(0)}</span>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={`font-medium text-sm ${colors.text} line-clamp-2`}>
              {title || content?.split('\n')[0] || 'Video Title'}
            </h3>
            <div className="flex items-center space-x-1 mt-1">
              <span className={`text-xs ${colors.textSecondary}`}>{brandName}</span>
              <span className={`text-xs ${colors.textSecondary}`}>•</span>
              <span className={`text-xs ${colors.textSecondary}`}>0 views</span>
              <span className={`text-xs ${colors.textSecondary}`}>•</span>
              <span className={`text-xs ${colors.textSecondary}`}>just now</span>
            </div>
            {content && content !== title && (
              <p className={`text-xs ${colors.textSecondary} mt-2 line-clamp-2`}>
                {content.length > 100 ? `${content.substring(0, 100)}...` : content}
              </p>
            )}
          </div>
          <div className="flex-shrink-0">
            <MoreHorizontal className={`w-5 h-5 ${colors.textSecondary}`} />
          </div>
        </div>

        {/* YouTube Actions */}
        <div className="flex items-center justify-between mt-3 px-2">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-1">
              <div className={`w-6 h-6 rounded-full border-2 ${colors.textSecondary} flex items-center justify-center`}>
                <div className="w-0 h-0 border-l-[6px] border-l-current border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent"></div>
              </div>
              <span className={`text-xs ${colors.textSecondary}`}>0</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className={`w-6 h-6 rounded-full border-2 ${colors.textSecondary} flex items-center justify-center`}>
                <div className="w-0 h-0 border-b-[6px] border-b-current border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent"></div>
              </div>
              <span className={`text-xs ${colors.textSecondary}`}>0</span>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <span className={`text-xs ${colors.textSecondary}`}>Share</span>
            <span className={`text-xs ${colors.textSecondary}`}>Save</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPlatformPost = () => {
    switch (platform) {
      case 'instagram':
        return renderInstagramPost();
      case 'facebook':
        return renderFacebookPost();
      case 'youtube':
        return renderYouTubePost();
      case 'twitter':
        return renderTwitterPost();
      default:
        return renderInstagramPost();
    }
  };

  return (
    <div className="relative">
      {/* Phone Frame */}
      <div className="relative mx-auto transform hover:scale-105 transition-transform duration-300" style={{ width: '280px', height: '560px' }}>
        {/* Phone Outer Frame */}
        <div className="absolute inset-0 bg-gray-900 rounded-[2.5rem] p-2 shadow-2xl">
          {/* Phone Screen */}
          <div className="w-full h-full bg-black rounded-[2rem] p-1">
            {/* Screen Content */}
            <div className={`w-full h-full ${colors.bg} rounded-[1.75rem] overflow-hidden relative`}>
              {/* Status Bar */}
              <div className={`${colors.cardBg} px-4 py-2 flex items-center justify-between text-xs font-medium`}>
                <span className={colors.text}>9:41</span>
                <div className="flex items-center space-x-1">
                  <div className="flex space-x-1">
                    <div className={`w-1 h-1 ${isDark ? 'bg-white' : 'bg-black'} rounded-full`}></div>
                    <div className={`w-1 h-1 ${isDark ? 'bg-white' : 'bg-black'} rounded-full`}></div>
                    <div className={`w-1 h-1 ${isDark ? 'bg-white' : 'bg-black'} rounded-full`}></div>
                    <div className={`w-1 h-1 ${isDark ? 'bg-gray-600' : 'bg-gray-300'} rounded-full`}></div>
                  </div>
                  <div className={`w-6 h-3 border ${isDark ? 'border-white' : 'border-black'} rounded-sm`}>
                    <div className={`w-4 h-1.5 ${isDark ? 'bg-white' : 'bg-black'} rounded-sm m-0.5`}></div>
                  </div>
                </div>
              </div>

              {/* App Header */}
              <div className={`${colors.cardBg} border-b ${colors.border} px-4 py-3`}>
                <div className="flex items-center justify-center">
                  <span className={`font-bold text-lg ${colors.text}`}>
                    {platform === 'instagram' && 'Instagram'}
                    {platform === 'facebook' && 'Facebook'}
                    {platform === 'youtube' && 'YouTube'}
                    {platform === 'twitter' && 'Twitter'}
                  </span>
                </div>
              </div>

              {/* Platform Post */}
              <div className="flex-1 overflow-y-auto">
                {renderPlatformPost()}
              </div>
            </div>
          </div>
        </div>

        {/* Phone Notch */}
        <div className="absolute top-2 left-1/2 transform -translate-x-1/2 w-20 h-6 bg-gray-900 rounded-b-2xl"></div>
      </div>
    </div>
  );
};

export default MobilePreview;